import {
  Avatar,
  AvatarRole,
  AvatarSize,
  DropdownMenu,
  Icon,
  IconName,
} from '@usealloy/component-library';
import { useContext, useMemo, useState } from 'react';

import { NotificationsContext, UserContext } from '~/dashboard/context';

import { Sidebar } from '../component/Sidebar';
import { BetaFeaturesMenu } from './BetaFeaturesMenu';
import { StyledFlyoutTriggerButton } from './FlyoutTriggerCaret';
import { ProductUpdatesModal } from './ProductUpdatesModal';
import { routes } from '~/helpers/routes';
import { filterNotifications } from '../../helpers';
import { Dot } from '~/components/dots';
import styled from 'styled-components';

interface AgentProfileMenuProps {
  expanded?: boolean;
}

const NotificationCount = styled(Dot)({
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--cl-font-size-1)',
});

/**
 * The SideNav Menu Item that holds the current agent's profile
 */
export const AgentProfileMenuItem = ({ expanded }: AgentProfileMenuProps) => {
  const { agent } = useContext(UserContext);

  const { notifications } = useContext(NotificationsContext);
  const unreadNotificationsCount = useMemo(
    () => (filterNotifications(notifications) ?? []).length || 0,
    [notifications],
  );

  const [showProductUpdates, setShowProductUpdates] = useState(false);

  const agentName = [agent?.firstName, agent?.lastName]
    .filter(Boolean)
    .join(' ');

  const agentRole = useMemo(() => {
    if (agent?.isEmployee) {
      return AvatarRole.Alloy;
    }
    if (agent?.adminAgentCustomerRole === 'Admin') {
      return AvatarRole.Admin;
    }
    if (agent?.isAgent) {
      return AvatarRole.User;
    }
    return AvatarRole.Default;
  }, [agent]);

  return (
    <>
      <Sidebar.MenuItem style={{ padding: '4px 16px 16px 16px' }}>
        <DropdownMenu
          modal={false}
          trigger={(
            <StyledFlyoutTriggerButton tooltip="Profile Menu" tooltipSide="top">
              <Avatar
                name={agentName}
                role={agentRole}
                size={AvatarSize.Medium}
                hideLabel
              />
              {expanded && (
                <>
                  <span className="fs-unmask" style={{ flex: 1, minWidth: 0 }}>
                    {agentName}
                  </span>
                  <Icon
                    icon={IconName.CaretRight}
                    style={{ flexShrink: 0, marginLeft: 'auto' }}
                  />
                </>
              )}
            </StyledFlyoutTriggerButton>
          )}
        >
          <DropdownMenu.Content
            side="right"
            style={{
              justifyContent: 'center',
              maxWidth: 400,
            }}
          >
            <BetaFeaturesMenu />
            <DropdownMenu.Item
              onSelect={() => {
                setShowProductUpdates(true);
              }}
            >
              <Icon icon={IconName.Gift} size="small" />
              <span>Product Updates</span>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                window.location.href = `${window.location.origin}${routes.v3.dashboard.notifications.list}`;
              }}
            >
              <Icon icon={IconName.Bell} size="small" />
              <span>Notifications</span>
              {unreadNotificationsCount > 0 && (
                <NotificationCount color="oklch(from var(--cl-background-attention) calc(l * 1.25) c h);" size={16}>
                  {unreadNotificationsCount}
                </NotificationCount>
              )}
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => {
                window.location.href = `${window.location.origin}/logout`;
              }}
            >
              <Icon icon={IconName.Power} size="small" />
              <span>Logout</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu>
      </Sidebar.MenuItem>

      <ProductUpdatesModal
        isOpen={showProductUpdates}
        onClose={() => setShowProductUpdates(false)}
      />
    </>
  );
};
